import React, { useEffect, useState } from 'react'

import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import { getCustomerDomain, notifyError } from '../../../../redux'

import { EnumPrecioUnitario } from '../../../../api'
import {
  EnumCommodity,
  EnumMarketType,
  EnumPriceCalculationType,
  EnumRedondeo
} from '../../../../api/Argentia/enums'
import { PriceExpressionValidator } from '../../../../components/shared'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../../../helpers'
import { useHttpError } from '../../../../hooks'
import { futurePricesMapperToDropdown } from '../../pricing.mapper'
import { PriceDetails } from '../../pricing.Model'
import { checkPriceCodeRepeated, fetchPricingListItems, savePrice } from '../../pricing.service'

import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { priceCalculationTypeMapper } from './editPriceDialog.helpers'
import { styles } from './editPriceDialog.styles'

export interface editPriceDialogProps {
  show: boolean
  onRefreshPage: () => void
  onShowEditPriceDialog: (show: boolean) => void
  price: PriceDetails
  priceType: EnumPriceType
  commodity: EnumCommodity
}

export const EditPriceDialog = ({
  show,
  onRefreshPage,
  onShowEditPriceDialog,
  price,
  priceType,
  commodity
}: editPriceDialogProps) => {
  const [t] = useTranslation([
    'priceExpressionValidator',
    'globalerisLibrary',
    'enums',
    'pricingDetails',
    'sharedTranslations'
  ])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()

  const [priceFields, setPriceFields] = useState<Partial<PriceDetails>>(price)

  const [futurePrices, setFuturePrices] = useState<SelectOption[]>([])
  const [expressionIsValid, setExpressionIsValid] = useState<boolean | undefined>(undefined)
  const [isPriceValidating, setIsPriceValidating] = useState<boolean>(false)
  const [selectedPriceCalculationType, setSelectedPriceCalculationType] = useState<
    EnumPriceCalculationType | undefined
  >(priceCalculationTypeMapper(price.priceCalculationType))
  const [expression, setExpression] = useState<string | undefined>(price.priceExpression)
  const [priceUnit, setPriceUnit] = useState<EnumPrecioUnitario | undefined>(price.precioUnitario)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [modifiedFields, setModifiedFields] = useState<boolean>(false)
  const [codeIsValid, setCodeIsValid] = useState<boolean | undefined>(true)
  const [isCheckingCode, setIsCheckingCode] = useState<boolean>(false)
  const [regexError, setRegexError] = useState<boolean>(false)

  const { showError, clearError, errorProps } = useHttpError()

  const showExpressionValidator =
    priceType === EnumPriceType.IndiceCalculado ||
    priceType === EnumPriceType.FormulaBase ||
    priceType === EnumPriceType.Dummy ||
    priceType === EnumPriceType.Formula

  const formSections: Section[] = [
    {
      inputs: [
        {
          name: 'code',
          label: t('pricingDetails:components.editPriceDialog.form.inputs.code'),
          type: DataType.TEXT,
          maxLength: 56,
          required: true,
          onChange: () => setModifiedFields(true),
          onBlur: value => {
            const code = value.toString()
            validateCode(code)
            setPriceFields({ ...priceFields, code: String(value) })
          },
          errorState: (!codeIsValid && typeof codeIsValid !== 'undefined') || regexError,
          errorText: !codeIsValid
            ? t('pricingDetails:components.editPriceDialog.notifications.checkCode.notValid')
            : t('sharedTranslations:priceCode.errorText')
        },
        {
          name: 'description',
          label: t('pricingDetails:components.editPriceDialog.form.inputs.description'),
          maxLength: 200,
          type: DataType.TEXT,
          onChange: () => setModifiedFields(true)
        },
        {
          name: 'precioUnitario',
          label: t('pricingDetails:components.editPriceDialog.form.inputs.priceUnit'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
          display:
            priceType !== EnumPriceType.IndiceCalculado && priceType !== EnumPriceType.FormulaBase,
          required:
            priceType !== EnumPriceType.IndiceCalculado && priceType !== EnumPriceType.FormulaBase,
          onChange: () => setModifiedFields(true)
        },
        {
          name: 'redondeo',
          label: t('pricingDetails:components.editPriceDialog.form.inputs.rounding'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
          required: true,
          onChange: () => setModifiedFields(true)
        },
        {
          name: 'marketType',
          label: t('pricingDetails:components.editPriceDialog.form.inputs.productType'),
          selectOptions:
            // For commodity Gas, hourly marketType option does not apply.
            commodity === EnumCommodity.GasNatural
              ? enumToSelectOptionArrayDefaultValues(EnumMarketType, 'enums:EnumMarketType', [
                  EnumMarketType.Diario,
                  EnumMarketType.Laborables,
                  EnumMarketType.Mensual,
                  EnumMarketType.Trimestral
                ])
              : enumToSelectOptionArray(EnumMarketType, 'enums:EnumMarketType'),
          type: DataType.SELECT,
          display: priceType === EnumPriceType.Cotizacion || priceType === EnumPriceType.Futuro,
          clearable: true,
          onChange: () => setModifiedFields(true)
        }
        // {
        //   name: 'relatedFuturePrice',
        //   label: t('pricingDetails:components.editPriceDialog.form.inputs.relatedFuturePrice'),
        //   type: DataType.SELECT,
        //   selectOptions: futurePrices,
        //   display: priceType === PriceTypes.cotization,
        //   onChange: () => setModifiedFields(true)
        // }
      ]
    }
  ]

  const formOptions: FormOptions = {
    elevation: 1,
    columns: 2
  }

  const isCodeFormatValid = (code: string): boolean => {
    const regex = /^[\w%_áéíóúñÁÉÍÓÚÑ]+$/
    return regex.test(code)
  }

  const validateCode = async (priceCode: string) => {
    if (priceCode === price.code) {
      // No need to validate if code is unchanged.
      setRegexError(false)
      setCodeIsValid(true)
      return
    }

    if (!isCodeFormatValid(priceCode)) {
      setRegexError(true)
      return
    } else {
      setRegexError(false)
    }

    setIsCheckingCode(true)
    const response = await checkPriceCodeRepeated(priceCode).finally(() => setIsCheckingCode(false))
    if (response.success) {
      if (response.data.isValid) {
        setCodeIsValid(true)
      } else setCodeIsValid(false)
    } else {
      notifyError(t('pricingDetails:components.editPriceDialog.notifications.checkCode.error'))
      setCodeIsValid(undefined)
      // error 502
    }
  }

  const fetchFuturePricesOptions = async () => {
    setIsFetching(true)

    await fetchPricingListItems(commodity, EnumPriceType.Futuro).then(response => {
      setIsFetching(false)
      if (response.success) {
        clearError()
        setFuturePrices(futurePricesMapperToDropdown(response.data))
      } else {
        setIsFetching(false)
        response.error &&
          showError({
            title: t('pricingDetails:components.editPriceDialog.form.title'),
            errorCode: response.error.code,
            onRefresh: async () => {
              fetchPricingListItems(commodity, EnumPriceType.Futuro)
            }
          })
      }
    })
  }

  const onFormSaveHandler = async () => {
    if (showExpressionValidator && !expressionIsValid) {
      notifyError(
        'No se ha podido guardar el precio. La expresión no es válida. Por favor, ingrese una expresión válida.'
      )
      return
    }

    const newPrice = {
      ...priceFields,
      expression: showExpressionValidator ? priceFields.priceExpression : null,
      priceCalculationType: showExpressionValidator ? priceFields.priceCalculationType : undefined
    }

    setIsFetching(true)
    savePrice(price.id, newPrice).then(response => {
      setIsFetching(false)
      if (response.success) {
        clearError()
        onRefreshPage()
        info('Se ha modificado el precio correctamente.')
        onShowEditPriceDialog(false)
      } else {
        response.error && notifyError('Error al intentar guardar el Precio. Inténtelo de nuevo.')
      }
    })
  }

  useEffect(() => {
    fetchFuturePricesOptions()
  }, [])

  return (
    <Dialog onClose={() => {}} open={show} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.title}>
        {t('pricingDetails:components.editPriceDialog.form.title')}
      </DialogTitle>
      <DialogContent className={classes.formWithOutToolbar} dividers>
        <Form
          sections={formSections}
          data={priceFields}
          options={{
            ...formOptions,
            analytics: {
              customer: getCustomerDomain(),
              view: t('pricingDetails:components.editPriceDialog.analytics')
            }
          }}
        />

        <PriceExpressionValidator
          showPriceUnit
          showVersion
          versionsButton
          show={showExpressionValidator}
          priceType={priceType}
          commodity={commodity}
          activeExpressionIsValid={true}
          activeExpression={expression}
          activePriceCalculationType={selectedPriceCalculationType}
          activePriceUnit={priceUnit}
          onSelectPriceUnit={value => {
            setPriceFields({ ...priceFields, precioUnitario: value })
            setModifiedFields(true)
          }}
          onSelectPriceCalculationType={value => {
            setPriceFields({ ...priceFields, priceCalculationType: value })
            setModifiedFields(true)
          }}
          onSelectValidExpression={value => {
            setPriceFields({ ...priceFields, priceExpression: value })
            setModifiedFields(true)
          }}
          onIsValid={value => {
            setExpressionIsValid(value)
            setModifiedFields(true)
          }}
          onIsLoading={isLoading => {
            setIsPriceValidating(isLoading)
          }}
          expressionInputSize={12}
          priceUnitInputSize={5}
          price={
            priceType === EnumPriceType.FormulaBase ||
            priceType === EnumPriceType.IndiceCalculado ||
            priceType === EnumPriceType.Formula ||
            priceType === EnumPriceType.Dummy
              ? price
              : undefined
          }
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setPriceFields(price)
            onShowEditPriceDialog(false)
          }}
        >
          {t('pricingDetails:components.editPriceDialog.mainActions.cancel')}
        </Button>
        <Button
          disabled={
            !modifiedFields || isCheckingCode || !codeIsValid || regexError || isPriceValidating
          }
          onClick={() => {
            onFormSaveHandler()
          }}
          color="primary"
        >
          {t('pricingDetails:components.editPriceDialog.mainActions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
